import { httpsCallable } from '@/lib/callableProxy';
import { functions } from '@/config/firebase';
import { appCheck } from '@/config/firebase';
import { getToken as getAppCheckToken } from 'firebase/app-check';

export interface ConnectOnboardingResponse {
  accountId: string;
  onboardingUrl: string;
}

export interface FinancingQuoteResponse {
  quoteId: string;
  token: string;
  shareUrl: string;
  qrCodePayload: string;
  checkoutMode?: 'full' | 'deposit_only';
  totalAmount?: number;
  depositAmount?: number;
  tiers?: Array<{ name: string; amount: number; description?: string }>;
  financedAmount: number;
  monthlyEstimate: number;
  payIn4Estimate: number;
  expiresAt: string;
}

export interface FinancingCheckoutResponse {
  sessionId: string;
  url: string;
}

interface CreateConnectOnboardingLinkData {
  workspaceId: string;
  refreshUrl: string;
  returnUrl: string;
  email?: string;
}

interface CreateFinancingQuoteData {
  workspaceId: string;
  sellerVertical?: string;
  customerName?: string;
  description?: string;
  totalAmount: number;
  depositAmount?: number;
  checkoutMode?: 'full' | 'deposit_only';
  tiers?: Array<{ name: string; amount: number; description?: string }>;
  currency?: string;
  expiresInHours?: number;
  tippingEnabled?: boolean;
}

interface CreateFinancingCheckoutData {
  workspaceId: string;
  quoteId: string;
  successUrl: string;
  cancelUrl: string;
}

interface CreateServiceTemplateData {
  workspaceId: string;
  name: string;
  description?: string;
  defaultAmount: number;
  currency?: string;
}

export interface PublicQuoteResponse {
  quoteId: string;
  businessName: string;
  description: string | null;
  checkoutMode?: 'full' | 'deposit_only';
  amount: number;
  totalAmount?: number;
  depositAmount?: number;
  balanceRemainingAmount?: number;
  chargeTodayAmount?: number;
  tiers?: Array<{
    id: string;
    name: string;
    amount: number;
    description?: string | null;
  }>;
  currency: string;
  tippingEnabled?: boolean;
  tipPolicy?: {
    maxPercentBps: number;
    maxAbsoluteCents: number;
  };
  preview: {
    payIn4Estimate: number;
    monthlyEstimate: number;
  };
  expiresAt: string | null;
  status: 'open' | 'expired' | 'paid';
}

export interface PublicCheckoutResponse {
  checkoutSessionId: string;
  checkoutUrl: string;
}

export interface OpenCheckoutWorkspaceResponse {
  workspaceId: string;
  slug: string;
  businessName: string;
  currency: string;
  minAmount: number;
  maxAmount: number;
}

export interface OpenCheckoutStartResponse extends PublicCheckoutResponse {
  quoteId: string;
  token: string;
  shareUrl: string;
}

export interface SellerQuoteStatus {
  quoteId: string;
  shareUrl: string | null;
  customerName: string | null;
  description: string | null;
  totalAmount: number;
  depositAmount: number;
  checkoutMode?: 'full' | 'deposit_only';
  tiers?: Array<{ name: string; amount: number; description?: string }>;
  financedAmount: number;
  monthlyEstimate: number;
  payIn4Estimate: number;
  currency: string;
  status: 'open' | 'expired' | 'paid' | 'deposit_paid' | 'checkout_started';
  createdAt: string | null;
  expiresAt: string | null;
  viewedAt: string | null;
  checkoutStartedAt: string | null;
  paidAt: string | null;
  viewCount: number;
  shareEvents: {
    copy: number;
    sms: number;
    qr: number;
    native: number;
  };
  followUpTemplates: {
    gentle: number;
    time_sensitive: number;
    help_first: number;
  };
  lastSharedAt: string | null;
  lastSharedChannel: 'copy' | 'sms' | 'qr' | 'native' | null;
}

export interface FinancingInsights {
  periodDays: number;
  isDataTruncated?: boolean;
  summary: {
    quotesCreated: number;
    viewedQuotes: number;
    checkoutStartedQuotes: number;
    paidQuotes: number;
  };
  rates: {
    viewRatePct: number;
    checkoutStartRatePct: number;
    paidRatePct: number;
  };
  levers: {
    followUpUsedOnPaidPct: number;
    topShareChannel: 'copy' | 'sms' | 'qr' | 'native' | 'none';
    topFollowUpTone: 'gentle' | 'time_sensitive' | 'help_first' | 'none';
  };
  tierPerformance?: {
    multiTierQuotesCreated: number;
    multiTierPaidQuotes: number;
    multiTierPaidRatePct: number;
    singleTierPaidRatePct: number;
    topSelectedTierName: string | null;
    selectionBreakdown: Array<{ tierName: string; count: number }>;
  };
  speed: {
    medianMinutesToFirstView: number | null;
    medianMinutesToCheckoutStart: number | null;
    medianMinutesToPaid: number | null;
  };
  benchmark: {
    networkSalesLiftPct: number | null;
    confidence: 'high' | 'medium' | 'low' | 'insufficient';
    note: string;
  };
}

export interface WorkspacePayoutItem {
  quoteId: string;
  sellerNetCents: number;
  amountTotalCents: number;
  platformFeeAmountCents: number;
  currency: string;
  status: string;
  createdAt: string | null;
  paidOutAt: string | null;
  refundedAt: string | null;
  disputeOpenedAt: string | null;
  disputeClosedAt: string | null;
  payoutNote: string | null;
  stripeCheckoutSessionId: string | null;
  stripePaymentIntentId: string | null;
}

export interface WorkspacePayoutSummary {
  pendingBalanceCents: number;
  pendingCount: number;
  paidOutBalanceCents: number;
  paidOutCount: number;
  refundedBalanceCents: number;
  disputedCount: number;
  hasConnectAccount: boolean;
}

export interface WorkspacePayoutPolicy {
  trustTier: 'new' | 'trusted' | 'high_trust';
  riskStatus: 'normal' | 'watch' | 'restricted' | 'frozen';
  payoutSchedule: 'daily' | 'weekly';
  payoutWeekday: number;
  payoutHoldDays: number;
  allowManualPayouts: boolean;
  manualPayoutCapCents: number;
  reserveBps: number;
}

export interface WorkspacePayoutResponse {
  payouts: WorkspacePayoutItem[];
  summary: WorkspacePayoutSummary;
  policy: WorkspacePayoutPolicy;
}

export interface ServiceTemplate {
  templateId: string;
  name: string;
  description: string;
  defaultAmount: number;
  currency: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface WorkspaceAutomationHealth {
  periodDays: number;
  webhooks: {
    configured: boolean;
    recentDeliveries: Array<{
      id: string;
      eventType: string;
      status: string;
      targetUrl: string | null;
      responseStatus: number | null;
      errorMessage: string | null;
      updatedAt: string | null;
    }>;
    summary: {
      delivered: number;
      failed: number;
      queued: number;
    };
  };
  widget: {
    clickTotalsByMode: Array<{ mode: string; count: number }>;
    totalClicks: number;
  };
}

const getFunctionsApiBase = (): string => {
  const explicitBase = import.meta.env.VITE_PUBLIC_API_BASE_URL as string | undefined;
  if (explicitBase && explicitBase.trim()) {
    return explicitBase.replace(/\/$/, '');
  }
  if (import.meta.env.DEV) {
    return '/api';
  }
  return '/api';
};

export async function createConnectOnboardingLink(
  payload: CreateConnectOnboardingLinkData
): Promise<ConnectOnboardingResponse> {
  const fn = httpsCallable<CreateConnectOnboardingLinkData, ConnectOnboardingResponse>(
    functions,
    'createConnectOnboardingLink'
  );
  const result = await fn(payload);
  return result.data;
}

export async function createFinancingQuote(
  payload: CreateFinancingQuoteData
): Promise<FinancingQuoteResponse> {
  const fn = httpsCallable<CreateFinancingQuoteData, FinancingQuoteResponse>(
    functions,
    'createFinancingQuote'
  );
  const result = await fn(payload);
  return result.data;
}

export async function createFinancingCheckoutSession(
  payload: CreateFinancingCheckoutData
): Promise<FinancingCheckoutResponse> {
  const fn = httpsCallable<CreateFinancingCheckoutData, FinancingCheckoutResponse>(
    functions,
    'createFinancingCheckoutSession'
  );
  const result = await fn(payload);
  return result.data;
}

export async function fetchPublicQuote(token: string): Promise<PublicQuoteResponse> {
  const response = await fetch(
    `${getFunctionsApiBase()}/publicQuoteRouter/quotes/${encodeURIComponent(token)}`
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch quote (${response.status})`);
  }
  return response.json() as Promise<PublicQuoteResponse>;
}

export async function createPublicCheckoutSession(
  token: string,
  email?: string,
  tipAmountCents?: number,
  selectedTierId?: string
): Promise<PublicCheckoutResponse> {
  const payload: { email?: string; tipAmountCents?: number; selectedTierId?: string } = {};
  if (email) payload.email = email;
  if (Number.isFinite(tipAmountCents)) payload.tipAmountCents = Math.max(0, Math.round(tipAmountCents as number));
  if (selectedTierId) payload.selectedTierId = selectedTierId;
  const response = await fetch(`${getFunctionsApiBase()}/publicQuoteRouter/quotes/${encodeURIComponent(token)}/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: `Request failed (${response.status})` }));
    throw new Error(data?.error || `Request failed (${response.status})`);
  }

  return response.json() as Promise<PublicCheckoutResponse>;
}

export async function fetchOpenCheckoutWorkspace(slug: string): Promise<OpenCheckoutWorkspaceResponse> {
  const response = await fetch(`${getFunctionsApiBase()}/publicQuoteRouter/open/${encodeURIComponent(slug)}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: `Request failed (${response.status})` }));
    throw new Error(data?.error || `Request failed (${response.status})`);
  }
  return response.json() as Promise<OpenCheckoutWorkspaceResponse>;
}

export async function createOpenCheckoutSession(payload: {
  slug: string;
  amount: number;
  description: string;
  email?: string;
  customerName?: string;
}): Promise<OpenCheckoutStartResponse> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (appCheck) {
    try {
      const tokenResult = await getAppCheckToken(appCheck, false);
      if (tokenResult?.token) {
        headers['X-Firebase-AppCheck'] = tokenResult.token;
      }
    } catch (error) {
      console.warn('[OpenLink] Failed to get App Check token for open checkout request.', error);
    }
  }

  const response = await fetch(`${getFunctionsApiBase()}/publicQuoteRouter/open/${encodeURIComponent(payload.slug)}/checkout`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      amount: payload.amount,
      description: payload.description,
      email: payload.email?.trim() || undefined,
      customerName: payload.customerName?.trim() || undefined,
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: `Request failed (${response.status})` }));
    throw new Error(data?.error || `Request failed (${response.status})`);
  }

  return response.json() as Promise<OpenCheckoutStartResponse>;
}

export async function listFinancingQuotes(
  workspaceId: string,
  limit = 10
): Promise<SellerQuoteStatus[]> {
  const fn = httpsCallable<{ workspaceId: string; limit?: number }, { quotes: SellerQuoteStatus[] }>(
    functions,
    'listFinancingQuotes'
  );
  const result = await fn({ workspaceId, limit });
  return result.data.quotes || [];
}

export async function getFinancingInsights(
  workspaceId: string,
  days = 30
): Promise<FinancingInsights> {
  const fn = httpsCallable<{ workspaceId: string; days?: number }, FinancingInsights>(
    functions,
    'getFinancingInsights'
  );
  const result = await fn({ workspaceId, days });
  return result.data;
}

export async function listWorkspacePayouts(
  workspaceId: string,
  limit = 100
): Promise<WorkspacePayoutResponse> {
  const fn = httpsCallable<{ workspaceId: string; limit?: number }, WorkspacePayoutResponse>(
    functions,
    'listWorkspacePayouts'
  );
  const result = await fn({ workspaceId, limit });
  return result.data;
}

export async function markPayoutAsPaidOut(
  workspaceId: string,
  quoteId: string,
  note?: string
): Promise<void> {
  const fn = httpsCallable<{ workspaceId: string; quoteId: string; note?: string }, { success: true }>(
    functions,
    'markPayoutAsPaidOut'
  );
  await fn({ workspaceId, quoteId, note });
}

export async function trackFinancingQuoteShareEvent(
  workspaceId: string,
  quoteId: string,
  channel: 'copy' | 'sms' | 'qr' | 'native',
  template?: 'gentle' | 'time_sensitive' | 'help_first'
): Promise<void> {
  const fn = httpsCallable<
    { workspaceId: string; quoteId: string; channel: 'copy' | 'sms' | 'qr' | 'native'; template?: 'gentle' | 'time_sensitive' | 'help_first' },
    { success: true }
  >(functions, 'trackFinancingQuoteShareEvent');
  await fn({ workspaceId, quoteId, channel, template });
}

export async function createServiceTemplate(
  payload: CreateServiceTemplateData
): Promise<{ success: true; templateId: string }> {
  const fn = httpsCallable<CreateServiceTemplateData, { success: true; templateId: string }>(
    functions,
    'createServiceTemplate'
  );
  const result = await fn(payload);
  return result.data;
}

export async function listServiceTemplates(
  workspaceId: string,
  limit = 20
): Promise<ServiceTemplate[]> {
  const fn = httpsCallable<{ workspaceId: string; limit?: number }, { templates: ServiceTemplate[] }>(
    functions,
    'listServiceTemplates'
  );
  const result = await fn({ workspaceId, limit });
  return result.data.templates || [];
}

export async function deleteServiceTemplate(
  workspaceId: string,
  templateId: string
): Promise<void> {
  const fn = httpsCallable<{ workspaceId: string; templateId: string }, { success: true }>(
    functions,
    'deleteServiceTemplate'
  );
  await fn({ workspaceId, templateId });
}

export async function getWorkspaceAutomationHealth(
  workspaceId: string,
  days = 7
): Promise<WorkspaceAutomationHealth> {
  const fn = httpsCallable<{ workspaceId: string; days?: number }, WorkspaceAutomationHealth>(
    functions,
    'getWorkspaceAutomationHealth'
  );
  const result = await fn({ workspaceId, days });
  return result.data;
}
